import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAddToCart, usePremadeQuizzes } from '@quizzach/api-hooks';
import {
	AuthenticationModal,
	Navigation,
	PremadeQuizCardComponent,
	ShareButton,
	useToast,
	AuthorMark,
	useGiftBasket,
} from '@quizzach/components';
import { IPremadeQuiz, RoutePath } from '@quizzach/interfaces';
import { IcBasket, IcDoc, IcGift, IcPlayOutline } from '@quizzach/assets';
import { Reveal, useParallax } from '@quizzach/motion';
import {
	GiftLine,
	scrollToTop,
	handleDownloadPremadeTemplate,
	sortQuizzesNewestFirst,
} from '@quizzach/utils';
import { useAuth, useQuizOwnershipContext } from '@quizzach/contexts';
import {
	BtnLine,
	BtnTeal,
	PremadePage,
	QBody,
	QHead,
	QMedia,
	QNote,
	QShareRow,
	QSimilar,
	Sk,
	SpecBar,
} from '@quizzach/styles';

/** How many suggestion tickets the shelf shows (one desktop row). */
const SIMILAR_COUNT = 4;

/**
 * Session history of visited premade pages, oldest first. Suggestions push
 * already-seen quizzes to the back of the queue so hopping between tickets
 * keeps surfacing new ones instead of looping over the same handful.
 */
const VISITED_KEY = 'premadeVisitedQuizzes';

const readVisitedPremade = (): string[] => {
	try {
		const raw = sessionStorage.getItem(VISITED_KEY);
		const list = raw ? JSON.parse(raw) : [];
		return Array.isArray(list) ? list : [];
	} catch {
		return [];
	}
};

const markVisitedPremade = (uuid: string) => {
	try {
		const list = readVisitedPremade().filter((id) => id !== uuid);
		list.push(uuid);
		sessionStorage.setItem(VISITED_KEY, JSON.stringify(list.slice(-30)));
	} catch {
		// storage unavailable (private mode etc.) — suggestions just lose dedup
	}
};

export const PremadeQuizzesById = () => {
	const { t } = useTranslation();
	const { uuid } = useParams();
	const { isAuthenticated } = useAuth();
	const location = useLocation();
	const navigate = useNavigate();
	const coverRef = useRef<HTMLImageElement>(null);

	const { loading, premadeQuizzesById, getPremadeQuizzesById } =
		usePremadeQuizzes();
	// separate hook instance so the full-list fetch (for suggestions) doesn't
	// touch the single quiz's loading state
	const { premadeQuizzes, getPremadeQuizzes } = usePremadeQuizzes();
	const {
		isQuizOwned,
		getUserQuizUuid,
		uuidSet: ownedUuids,
	} = useQuizOwnershipContext();

	const [showModal, setShowModal] = useState(false);
	const { showToast: showCartToast } = useToast();

	const isOwned = isQuizOwned(premadeQuizzesById);

	const { addGift, countOf } = useGiftBasket();
	const [giftBusy, setGiftBusy] = useState(false);
	const giftLine: GiftLine = {
		kind: 'premade',
		uuid: premadeQuizzesById.uuid,
	};

	useParallax(coverRef, 0.1);

	const { loading: addToCartLoading, addToCart } = useAddToCart({
		type: 'premade',
		uuid: premadeQuizzesById.uuid,
	});

	const performAdd = async () => {
		const { success } = await addToCart();

		if (!success) {
			showCartToast({
				severity: 'error',
				message: t('toast.problem'),
				description: t('toast.notAddedText'),
				actionLabel: t('common.retry'),
				onAction: () => {
					void performAdd();
				},
			});
		} else {
			showCartToast({
				severity: 'success',
				message: t('toast.addedToCart'),
				description: premadeQuizzesById.title,
				actionLabel: t('toast.cart'),
				onAction: () => navigate(RoutePath.Summary),
			});
		}
	};

	const handleAddToBasket = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();

		if (!isAuthenticated) {
			setShowModal(true);
		} else {
			void performAdd();
		}
	};

	// a clicked-again button adds another gift copy
	const handleBuyAsGift = async (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		if (!isAuthenticated) {
			setShowModal(true);
			return;
		}
		if (giftBusy) return;
		setGiftBusy(true);
		await addGift(giftLine, premadeQuizzesById.title);
		setGiftBusy(false);
	};

	const shareData = {
		text: t('premade.single.shareText', { title: premadeQuizzesById?.title }),
		url: `https://quizzach.com/premade/quiz/${premadeQuizzesById?.uuid}`,
	};

	useEffect(() => {
		if (uuid) {
			markVisitedPremade(uuid);
			getPremadeQuizzesById(uuid);
		}
	}, [uuid]);

	useEffect(() => {
		getPremadeQuizzes();
	}, []);

	// suggestions ranked by shared tags (heaviest) and same author, padded
	// with the newest of the rest so the shelf always holds a full row;
	// quizzes visited this session drop to the back (oldest visit first) so
	// clicking through suggestions doesn't orbit the same few tickets, and
	// quizzes already in the profile collection sink below even those
	const similarQuizzes = useMemo(() => {
		if (!premadeQuizzes?.length || !premadeQuizzesById?.uuid) return [];

		const ownTags = new Set(
			(premadeQuizzesById.primary_thematic_tags ?? [])
				.map((tag) => tag.name)
				.filter(Boolean)
		);
		const ownAuthor = premadeQuizzesById.author?.display_name_slug;

		const score = (quiz: IPremadeQuiz) => {
			const sharedTags = (quiz.primary_thematic_tags ?? []).filter((tag) =>
				ownTags.has(tag.name)
			).length;
			const sameAuthor =
				ownAuthor && quiz.author?.display_name_slug === ownAuthor ? 1 : 0;
			return sharedTags * 2 + sameAuthor;
		};

		const candidates = premadeQuizzes.filter(
			(quiz) => quiz.uuid !== premadeQuizzesById.uuid
		);
		const related = sortQuizzesNewestFirst(
			candidates.filter((quiz) => score(quiz) > 0)
		).sort((a, b) => score(b) - score(a));
		const rest = sortQuizzesNewestFirst(
			candidates.filter((quiz) => score(quiz) === 0)
		);

		const visited = readVisitedPremade();
		const visitedSet = new Set(visited);
		const ranked = [...related, ...rest];
		const unowned = ranked.filter((quiz) => !ownedUuids.has(quiz.uuid));
		const fresh = unowned.filter((quiz) => !visitedSet.has(quiz.uuid));
		const seen = unowned
			.filter((quiz) => visitedSet.has(quiz.uuid))
			.sort((a, b) => visited.indexOf(a.uuid) - visited.indexOf(b.uuid));
		const owned = ranked.filter((quiz) => ownedUuids.has(quiz.uuid));

		return [...fresh, ...seen, ...owned].slice(0, SIMILAR_COUNT);
	}, [premadeQuizzes, premadeQuizzesById, ownedUuids]);

	useEffect(() => {
		if (premadeQuizzesById?.uuid === uuid) {
			scrollToTop();
		}
	}, [premadeQuizzesById, uuid]);

	const level = premadeQuizzesById?.level ?? 0;
	const levelLabel =
		level === 1
			? t('premade.single.levelEasy')
			: level === 2
				? t('premade.single.levelMedium')
				: level === 3
					? t('premade.single.levelHard')
					: '';

	return (
		<PremadePage>
			{showModal && (
				<AuthenticationModal
					closeModal={() => setShowModal(false)}
					path={location.pathname}
				/>
			)}
			<Navigation />

			{loading && (
				<div aria-hidden='true'>
					<QHead>
						<div>
							<Sk style={{ height: 14, width: 180, marginBottom: 18 }} />
							<Sk
								style={{ height: 'clamp(46px, 7.4vw, 110px)', maxWidth: 720 }}
							/>
						</div>
					</QHead>
					<Sk
						style={{
							height: 'min(70vh, 680px)',
							margin: '0 var(--gut)',
							borderRadius: 'var(--r)',
						}}
					/>
				</div>
			)}
			{!loading && (
				<>
					<QHead>
						<div>
							<Reveal className='tags'>
								{premadeQuizzesById?.primary_thematic_tags?.map(
									(tag, index) => (
										<Link
											key={index}
											to={`${RoutePath.PremadeQuizzes}/tag/${tag.name}`}
										>
											{tag.name}
										</Link>
									)
								)}
							</Reveal>
							<Reveal as='h1'>{premadeQuizzesById?.title}</Reveal>
						</div>
						<Reveal as='span' className='hand' delay={1}>
							{premadeQuizzesById?.short_description}
						</Reveal>
					</QHead>

					<QShareRow>
						<ShareButton shareData={shareData} iconOnly iconSize={22} />
					</QShareRow>

					<Reveal>
						<QMedia data-darkzone>
							<img
								ref={coverRef}
								src={`${
									import.meta.env.VITE_ASSETS_URL
								}/thematic_quizzes/${premadeQuizzesById?.cover_photo}`}
								alt={premadeQuizzesById?.title}
							/>
							<span className='shade-top' />
						</QMedia>
					</Reveal>

					<SpecBar>
						<div className='cell'>
							<span className='k'>{t('premade.single.author')}</span>
							<span className='v hand'>
								<AuthorMark
									displayName={premadeQuizzesById?.author?.display_name}
									displayNameSlug={
										premadeQuizzesById?.author?.display_name_slug
									}
								/>
							</span>
						</div>
						<div className='cell'>
							<span className='k'>{t('premade.single.questionCount')}</span>
							<span className='v'>
								{premadeQuizzesById?.number_of_questions - 1}
							</span>
						</div>
						{level > 0 && (
							<div className='cell flex'>
								<span className='k'>{t('premade.single.difficulty')}</span>
								<span className='v'>
									{levelLabel}
									<span className='lvl'>
										<i className={`l1${level === 1 ? ' f' : ''}`} />
										<i className={`l2${level === 2 ? ' f' : ''}`} />
										<i className={`l3${level === 3 ? ' f' : ''}`} />
									</span>
								</span>
							</div>
						)}
						{premadeQuizzesById?.price && (
							<div className='cell'>
								<span className='k'>{t('premade.single.price')}</span>
								<span className='v price'>
									{premadeQuizzesById.price.replace('.', ',')} EUR
								</span>
							</div>
						)}
					</SpecBar>

					<QBody>
						<Reveal as='p'>{premadeQuizzesById?.long_description}</Reveal>
						<Reveal className='acts' delay={1}>
							{isOwned ? (
								<BtnTeal
									type='button'
									onClick={() =>
										navigate(
											`/quiz/${getUserQuizUuid(premadeQuizzesById)}/play`
										)
									}
								>
									<IcPlayOutline size={19} />
									{t('premade.single.startQuiz')}
								</BtnTeal>
							) : (
								<BtnTeal type='button' onClick={handleAddToBasket}>
									{addToCartLoading ? (
										<span className='loader'></span>
									) : (
										<>
											<IcBasket size={19} />
											{t('premade.single.addToCart')}
										</>
									)}
								</BtnTeal>
							)}
							<BtnLine
								type='button'
								aria-busy={giftBusy}
								onClick={handleBuyAsGift}
							>
								<IcGift size={19} />
								{countOf(giftLine)
									? t('premade.single.buyAnotherGift')
									: t('premade.single.buyAsGift')}
							</BtnLine>
							<ShareButton shareData={shareData} className='share-inline' />
						</Reveal>
					</QBody>

					<QNote
						type='button'
						onClick={() => handleDownloadPremadeTemplate(premadeQuizzesById)}
					>
						<IcDoc size={22} />
						{t('premade.single.printableTemplate')}
					</QNote>

					{similarQuizzes.length > 0 && (
						<QSimilar>
							<Reveal className='shead'>
								<span className='hand'>{t('premade.single.similarHand')}</span>
							</Reveal>
							<Reveal className='sgrid' delay={1}>
								{similarQuizzes.map((quiz) => (
									<PremadeQuizCardComponent key={quiz.uuid} quiz={quiz} />
								))}
							</Reveal>
						</QSimilar>
					)}
				</>
			)}
		</PremadePage>
	);
};
